import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { RotateCcw } from 'lucide-react';
import { isLscBillingUiEnabled } from '../../config/featureFlags';
import BillingDisabledBanner from '../components/BillingDisabledBanner';
import BillingSupportContact from '../billingSupportContact';
import { LSC_BILLING_CANCELED_BODY } from '../billingCopy';
import { lscPath } from '../routing';
import { buildSetupSubscriptionReturnPath, resolveCheckoutLeagueId } from '../setupSubscriptionReturn';
import { useLscContentColumn } from '../lscContentColumn';

const LscBillingCanceledPage: React.FC = () => {
  useLscContentColumn('narrow');
  const billingUiEnabled = isLscBillingUiEnabled();
  const [searchParams] = useSearchParams();
  const leagueId = useMemo(
    () => resolveCheckoutLeagueId(searchParams.get('league_id')),
    [searchParams]
  );
  const setupReturnPath = buildSetupSubscriptionReturnPath(leagueId);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="lsc-billing-canceled-page">
      <div className="card space-y-4">
        <div className="flex items-start gap-3">
          <RotateCcw className="h-5 w-5 text-secondary-400 mt-0.5 shrink-0" aria-hidden />
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Checkout canceled</h1>
            <p className="text-sm text-secondary-300">{LSC_BILLING_CANCELED_BODY}</p>
          </div>
        </div>

        {!billingUiEnabled && <BillingDisabledBanner variant="ui" />}

        <div className="flex flex-col sm:flex-row flex-wrap gap-3 pt-2">
          {setupReturnPath ? (
            <Link
              to={setupReturnPath}
              className="btn-primary min-h-11 inline-flex items-center justify-center px-4"
              data-testid="lsc-billing-canceled-return-setup"
            >
              Return to league setup
            </Link>
          ) : null}
          <Link
            to={lscPath('/pricing')}
            className="btn-secondary min-h-11 inline-flex items-center justify-center px-4"
            data-testid="lsc-billing-retry-cta"
          >
            {billingUiEnabled ? 'Return to pricing' : 'View pricing'}
          </Link>
          <Link
            to={lscPath('/billing')}
            className="btn-secondary min-h-11 inline-flex items-center justify-center px-4"
          >
            Billing status
          </Link>
          <Link
            to={lscPath('/leagues/new')}
            className="btn-secondary min-h-11 inline-flex items-center justify-center px-4"
            data-testid="lsc-billing-canceled-create-league"
          >
            Start a league
          </Link>
        </div>
      </div>

      <BillingSupportContact />
    </div>
  );
};

export default LscBillingCanceledPage;
